import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useUrlFilters from 'hooks/useUrlFilters';
import { Button, Input, Select, Tag, Space, Card, Statistic, Row, Col, Badge, Drawer, Form, Descriptions, Timeline, Divider, Modal, Tabs, Checkbox } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  EyeOutlined,
  UserOutlined,
  SwapOutlined,
  DeleteOutlined,
  MessageOutlined,
  FileAddOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  loadTickets,
  loadTicketStatistics,
  loadTicketById,
  updateTicket,
  deleteTicket,
  assignTicket,
  changeTicketStatus,
  addTicketComment,
  convertToTasks
} from 'store/slices/SupportSlice';
import { formatDistanceToNow, format } from 'date-fns';
import axios from 'axios';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import { CKEditor } from 'ckeditor4-react';

const { Option } = Select;
const { TextArea } = Input;

// Search, filters, status tab and page live in the URL so a filtered ticket
// list can be shared.
const URL_FILTERS = {
  search: { type: 'string' },
  status: { type: 'string', default: 'all' },
  priority: { type: 'string' },
  category: { type: 'string' },
  project: { type: 'string' },
  assignedToMe: { type: 'boolean', key: 'assigned_to_me', default: false },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const SupportTickets = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tickets = useSelector(state => state.support.tickets);
  const ticketsLoading = useSelector(state => state.support.ticketsLoading);
  const pagination = useSelector(state => state.support.ticketsPagination);
  const statistics = useSelector(state => state.support.ticketStatistics);
  const ticketStatuses = useSelector(state => state.support.ticketStatuses);
  const ticketCategories = useSelector(state => state.support.ticketCategories);
  const selectedTicket = useSelector(state => state.support.selectedTicket);

  // CKEditor configuration
  const editorConfig = {
    extraPlugins: ['tableresize', 'uploadimage'],
    removePlugins: [
      'exportpdf',
      'forms',
      'smiley',
      'language',
      'iframe',
      'about',
      'save',
      'preview',
      'print',
    ],
    filebrowserImageUploadUrl:
      process.env.REACT_APP_BASE_URL + `/template/upload_template_image/`,
    fileTools_requestHeaders: axios.defaults.headers.common,
    height: 300,
    toolbar: [
      { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike'] },
      { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent'] },
      { name: 'links', items: ['Link', 'Unlink'] },
      { name: 'insert', items: ['Image', 'Table'] },
      { name: 'styles', items: ['Format'] },
      { name: 'colors', items: ['TextColor', 'BGColor'] },
    ]
  };

  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const searchValue = urlFilters.search || '';
  const [searchLive, setSearchLive] = useState(searchValue);
  useEffect(() => setSearchLive(searchValue), [searchValue]);
  const filters = useMemo(() => ({
    priority: urlFilters.priority,
    category: urlFilters.category,
    project: urlFilters.project,
    assigned_to_me: urlFilters.assignedToMe,
  }), [urlFilters.priority, urlFilters.category, urlFilters.project, urlFilters.assignedToMe]);
  const currentPage = urlFilters.page;
  const pageSize = Math.min(urlFilters.pageSize, MAX_PAGE_SIZE);
  const activeStatusTab = urlFilters.status;

  // Drawer states
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);

  // Inline editing states
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);
  const [editingAssignedTo, setEditingAssignedTo] = useState(false);
  const [, setEditingCategory] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [descriptionEditor, setDescriptionEditor] = useState(null);

  // Modal visibility states
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);

  // Forms
  const [assignForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [taskForm] = Form.useForm();

  useEffect(() => {
    dispatch(loadTicketStatistics());
    loadEmployeesData();
    loadProjectsData();
    loadAccountsData();
  }, []);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, pageSize, searchValue, activeStatusTab, filters]);

  // Handle URL params for drawer
  useEffect(() => {
    const viewId = searchParams.get('viewId');

    if (viewId) {
      setDrawerVisible(true);
      dispatch(loadTicketById(viewId));
    } else {
      setDrawerVisible(false);
      setEditingTitle(false);
      setEditingDescription(false);
    }
  }, [searchParams]);

  // Set inline edit values when ticket loads
  useEffect(() => {
    if (selectedTicket) {
      setTitleValue(selectedTicket.title || '');
      setDescriptionValue(selectedTicket.description || '');
    }
  }, [selectedTicket]);

  const loadData = (page) => {
    const statusFilter = activeStatusTab !== 'all' ? activeStatusTab : null;
    dispatch(loadTickets({ page, searchValue, page_size: pageSize, filters: { ...filters, status: statusFilter } }));
  };

  const handleStatusTabChange = (key) => {
    setUrlFilters({ status: key, page: 1 });
  };

  const handleSearch = (value) => {
    setUrlFilters({ search: value, page: 1 });
  };

  // Search sits outside the filter panel, so it is neither counted nor cleared.
  const activeFilterCount =
    (filters.priority ? 1 : 0) +
    (filters.category ? 1 : 0) +
    (filters.project ? 1 : 0) +
    (filters.assigned_to_me ? 1 : 0);

  const clearFilters = () => {
    setUrlFilters({ priority: null, category: null, project: null, assignedToMe: null, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setUrlFilters({ [key === 'assigned_to_me' ? 'assignedToMe' : key]: value, page: 1 });
  };

  const loadEmployeesData = async () => {
    try {
      const response = await axios.get('/employee/employee/?page_size=100');
      setEmployees(response.data.results || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const loadProjectsData = async () => {
    try {
      const response = await axios.get('/project_board/project/?page_size=100');
      setProjects(response.data.results || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadAccountsData = async () => {
    try {
      const response = await axios.get('/accounts/account/?page_size=100');
      setAccounts(response.data.results || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const closeDrawer = () => {
    // Drop only the drawer param so the list filters stay in the URL.
    const params = new URLSearchParams(searchParams);
    params.delete('viewId');
    setSearchParams(params);
    setDrawerVisible(false);
    setEditingTitle(false);
    setEditingDescription(false);
    setEditingAccount(false);
    setEditingAssignedTo(false);
    setEditingCategory(false);
  };

  const openViewDrawer = (ticketId) => {
    const params = new URLSearchParams(searchParams);
    params.set('viewId', ticketId);
    setSearchParams(params);
  };

  // Inline edit handlers
  const handleTitleSave = async () => {
    if (!titleValue.trim() || titleValue === selectedTicket.title) {
      setEditingTitle(false);
      setTitleValue(selectedTicket.title);
      return;
    }
    try {
      await dispatch(updateTicket({
        ticketId: selectedTicket.id,
        ticketData: { title: titleValue }
      })).unwrap();
      setEditingTitle(false);
      dispatch(loadTicketById(selectedTicket.id));
    } catch (error) {
      console.error('Failed to update title:', error);
      setTitleValue(selectedTicket.title);
    }
  };

  const handleDescriptionSave = async () => {
    const editorData = descriptionEditor ? descriptionEditor.getData() : descriptionValue;
    if (!editorData.trim() || editorData === selectedTicket.description) {
      setEditingDescription(false);
      setDescriptionValue(selectedTicket.description);
      setDescriptionEditor(null);
      return;
    }
    try {
      await dispatch(updateTicket({
        ticketId: selectedTicket.id,
        ticketData: { description: editorData }
      })).unwrap();
      setEditingDescription(false);
      setDescriptionEditor(null);
      dispatch(loadTicketById(selectedTicket.id));
    } catch (error) {
      console.error('Failed to update description:', error);
      setDescriptionValue(selectedTicket.description);
      setDescriptionEditor(null);
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await dispatch(updateTicket({
        ticketId: selectedTicket.id,
        ticketData: { priority }
      })).unwrap();
      dispatch(loadTicketById(selectedTicket.id));
    } catch (error) {
      console.error('Failed to update priority:', error);
    }
  };

  const handleCategoryChange = async (category) => {
    try {
      await dispatch(updateTicket({
        ticketId: selectedTicket.id,
        ticketData: { category }
      })).unwrap();
      dispatch(loadTicketById(selectedTicket.id));
      setEditingCategory(false);
    } catch (error) {
      console.error('Failed to update category:', error);
    }
  };

  const handleAccountChange = async (account) => {
    try {
      await dispatch(updateTicket({
        ticketId: selectedTicket.id,
        ticketData: { account }
      })).unwrap();
      dispatch(loadTicketById(selectedTicket.id));
      setEditingAccount(false);
    } catch (error) {
      console.error('Failed to update account:', error);
    }
  };

  const handleAssignedToChange = async (assigned_to) => {
    try {
      await dispatch(updateTicket({
        ticketId: selectedTicket.id,
        ticketData: { assigned_to }
      })).unwrap();
      dispatch(loadTicketById(selectedTicket.id));
      setEditingAssignedTo(false);
    } catch (error) {
      console.error('Failed to update assigned to:', error);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Ticket',
      content: 'Are you sure you want to delete this ticket? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dispatch(deleteTicket(selectedTicket.id)).unwrap();
          closeDrawer();
          loadData(currentPage);
        } catch (error) {
          console.error('Failed to delete ticket:', error);
        }
      }
    });
  };

  const handleAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      await dispatch(assignTicket({
        ticketId: selectedTicket.id,
        employee_id: values.employee_id,
        assigned_team: values.assigned_team
      })).unwrap();
      setAssignModalVisible(false);
      assignForm.resetFields();
      dispatch(loadTicketById(selectedTicket.id));
    } catch (error) {
      console.error('Failed to assign:', error);
    }
  };

  const handleStatusChange = async () => {
    try {
      const values = await statusForm.validateFields();
      await dispatch(changeTicketStatus({
        ticketId: selectedTicket.id,
        status_id: values.status_id,
        sub_status_id: values.sub_status_id
      })).unwrap();
      setStatusModalVisible(false);
      statusForm.resetFields();
      dispatch(loadTicketById(selectedTicket.id));
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const handleAddComment = async () => {
    try {
      const values = await commentForm.validateFields();
      await dispatch(addTicketComment({
        ticketId: selectedTicket.id,
        comment_type: values.is_internal ? 'INTERNAL_NOTE' : 'EMPLOYEE_REPLY',
        message: values.message,
        is_internal: values.is_internal || false
      })).unwrap();
      setCommentModalVisible(false);
      commentForm.resetFields();
      dispatch(loadTicketById(selectedTicket.id));
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleConvertToTasks = async () => {
    try {
      const values = await taskForm.validateFields();
      const tasks = [{
        project_id: values.project_id,
        title: values.title || selectedTicket.title,
        description: values.description || selectedTicket.description,
        notes: values.notes
      }];
      await dispatch(convertToTasks({ ticketId: selectedTicket.id, tasks })).unwrap();
      setTaskModalVisible(false);
      taskForm.resetFields();
      dispatch(loadTicketById(selectedTicket.id));
    } catch (error) {
      console.error('Failed to convert to tasks:', error);
    }
  };

  const getStatusColor = (statusName) => {
    const name = statusName?.toLowerCase() || '';
    if (name.includes('closed') || name.includes('resolved')) return 'green';
    if (name.includes('progress')) return 'blue';
    if (name.includes('pending')) return 'orange';
    if (name.includes('new')) return 'cyan';
    return 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'CRITICAL': 'red',
      'HIGH': 'orange',
      'MEDIUM': 'blue',
      'LOW': 'default'
    };
    return colors[priority] || 'default';
  };

  const getSLAStatusColor = (slaStatus) => {
    if (slaStatus === 'BREACHED') return 'red';
    if (slaStatus === 'APPROACHING_BREACH') return 'orange';
    if (slaStatus === 'WITHIN_SLA') return 'green';
    return 'default';
  };

  const columns = [
    {
      title: 'Ticket #',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      width: 120,
      render: (text) => <span style={{ fontFamily: 'monospace', fontWeight: 500, fontSize: 12 }}>{text}</span>
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority, record) => (
        <Tag color={getPriorityColor(priority)} style={{ fontSize: 11 }}>
          {record.priority_display || priority}
        </Tag>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category',
      width: 120,
      ellipsis: true
    },
    {
      title: 'Project',
      dataIndex: 'project_code',
      key: 'project',
      width: 90,
      render: (code, record) => code ? (
        <Tag color="geekblue" style={{ fontSize: 11 }} title={record.project_name}>{code}</Tag>
      ) : '-'
    },
    {
      title: 'Account',
      dataIndex: 'account_name',
      key: 'account',
      ellipsis: true,
      width: 130
    },
    {
      title: 'Assigned',
      dataIndex: 'assigned_to_name',
      key: 'assigned_to',
      width: 120,
      ellipsis: true,
      render: (text) => text || <span style={{ color: '#999', fontSize: 12 }}>Unassigned</span>
    },
    {
      title: 'SLA',
      dataIndex: 'sla_status',
      key: 'sla_status',
      width: 85,
      render: (slaStatus, record) => {
        if (!slaStatus || slaStatus === 'NO_SLA') return '-';
        return (
          <Tag color={getSLAStatusColor(slaStatus)} style={{ fontSize: 11 }}>
            {record.sla_status_display || slaStatus}
          </Tag>
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date) => <span style={{ fontSize: 12 }}>{formatDistanceToNow(new Date(date), { addSuffix: true })}</span>
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            openViewDrawer(record.id);
          }}
        />
      )
    }
  ];

  return (
    <div style={{ padding: '12px 16px' }}>
      {/* Statistics */}
      {statistics && (
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>Total Tickets</span>}
                value={statistics.total}
                prefix={<Badge status="processing" />}
                valueStyle={{ fontSize: 20 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>Assigned to Me</span>}
                value={statistics.assigned_to_me}
                valueStyle={{ color: '#1890ff', fontSize: 20 }}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>Unassigned</span>}
                value={statistics.unassigned}
                valueStyle={{ color: '#faad14', fontSize: 20 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>SLA Breached</span>}
                value={statistics.by_sla_status?.BREACHED || 0}
                valueStyle={{ color: '#f5222d', fontSize: 20 }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters & Search */}
      <Card size="small" style={{ marginBottom: 8 }}>
        <FilterBar
          searchValue={searchLive}
          onSearchChange={setSearchLive}
          onSearch={handleSearch}
          searchPlaceholder="Search tickets"
          activeCount={activeFilterCount}
          onClear={clearFilters}
          title="Filter Tickets"
          addLabel="Create Ticket"
          onAdd={() => navigate('/support/tickets/new')}
          onRefresh={() => loadData(currentPage)}
        >
          <FilterField label="Priority">
            <Select
              placeholder="Priority"
              value={filters.priority || undefined}
              onChange={(value) => handleFilterChange('priority', value)}
              allowClear
            >
              <Option value="CRITICAL">Critical</Option>
              <Option value="HIGH">High</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="LOW">Low</Option>
            </Select>
          </FilterField>

          <FilterField label="Category">
            <Select
              placeholder="Category"
              value={filters.category || undefined}
              onChange={(value) => handleFilterChange('category', value)}
              allowClear
            >
              {ticketCategories.map(cat => (
                <Option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </FilterField>

          <FilterField label="Project">
            <Select
              placeholder="Project"
              value={filters.project || undefined}
              onChange={(value) => handleFilterChange('project', value)}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {projects.filter(p => p.is_support_enabled).map(proj => (
                <Option key={proj.id} value={String(proj.id)}>
                  {proj.project_code} - {proj.name}
                </Option>
              ))}
            </Select>
          </FilterField>

          <FilterField>
            <Button
              icon={<UserOutlined />}
              onClick={() => handleFilterChange('assigned_to_me', !filters.assigned_to_me)}
              type={filters.assigned_to_me ? 'primary' : 'default'}
              block
            >
              Assigned to Me
            </Button>
          </FilterField>
        </FilterBar>
      </Card>

      {/* Table */}
      <Card
        size="small"
        title={
          <Tabs
            activeKey={activeStatusTab}
            onChange={handleStatusTabChange}
            size="small"
            style={{ marginBottom: 0 }}
            items={[
              { key: 'all', label: 'All' },
              ...ticketStatuses
                .filter(status => status.name?.toLowerCase() !== 'new')
                .map(status => ({
                  key: String(status.id),
                  label: status.name
                }))
            ]}
          />
        }
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={columns}
          dataSource={tickets}
          loading={ticketsLoading}
          rowKey="id"
          size="small"
          scroll={{ x: 1400 }}
          pagination={{
            current: currentPage,
            total: pagination.totalCount,
            pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} tickets`,
            // A new page size starts again from page 1
            onChange: (page, size) => setUrlFilters({ page: size !== pageSize ? 1 : page, pageSize: size }),
            size: 'small'
          }}
          onRow={(record) => ({
            onClick: () => openViewDrawer(record.id)
          })}
          style={{ cursor: 'pointer' }}
        />
      </Card>

      {/* Ticket Drawer */}
      <Drawer
        title="Ticket Details"
        open={drawerVisible}
        onClose={closeDrawer}
        width={800}
        bodyStyle={{ paddingBottom: 80, overflowY: 'auto' }}
        extra={
          <Space size="small">
            <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/support/tickets/${selectedTicket?.id}`)}>
              Full Details
            </Button>
            <Button size="small" icon={<SwapOutlined />} onClick={() => setStatusModalVisible(true)}>
              Status
            </Button>
            <Button size="small" icon={<FileAddOutlined />} onClick={() => setTaskModalVisible(true)}>
              To Task
            </Button>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
          </Space>
        }
      >
        {selectedTicket ? (
          <div>
              {/* Ticket Number */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>TICKET NUMBER</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 16 }}>
                  {selectedTicket.ticket_number}
                </div>
              </Card>

              {/* Title - Inline Editable */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>TITLE</div>
                {editingTitle ? (
                  <Input
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onBlur={handleTitleSave}
                    onPressEnter={handleTitleSave}
                    autoFocus
                    placeholder="Enter ticket title"
                  />
                ) : (
                  <h3
                    onClick={() => setEditingTitle(true)}
                    style={{ margin: 0, cursor: 'pointer', padding: '4px 8px', borderRadius: 4 }}
                    onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    {selectedTicket.title}
                  </h3>
                )}
              </Card>

              {/* Description - Inline Editable with CKEditor */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>DESCRIPTION</div>
                {editingDescription ? (
                  <div>
                    <CKEditor
                      initData={selectedTicket.description || '<p>Enter ticket description...</p>'}
                      config={editorConfig}
                      editorUrl={
                        process.env.REACT_APP_BASE_URL +
                        '/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js'
                      }
                      onInstanceReady={(event) => {
                        setDescriptionEditor(event.editor);
                      }}
                    />
                    <div style={{ marginTop: 8, textAlign: 'right' }}>
                      <Space>
                        <Button size="small" onClick={() => {
                          setEditingDescription(false);
                          setDescriptionEditor(null);
                        }}>
                          Cancel
                        </Button>
                        <Button size="small" type="primary" onClick={handleDescriptionSave}>
                          Save
                        </Button>
                      </Space>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => setEditingDescription(true)}
                    style={{
                      padding: '8px 12px',
                      background: '#fafafa',
                      borderRadius: 4,
                      cursor: 'pointer',
                      minHeight: 100
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.background = '#fafafa'}
                    dangerouslySetInnerHTML={{ __html: selectedTicket.description || '<span style="color: #999">Click to add description...</span>' }}
                  />
                )}
              </Card>

              {/* Priority and Category - Inline Editable */}
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Card size="small">
                    <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>PRIORITY</div>
                    <Select
                      value={selectedTicket.priority}
                      onChange={handlePriorityChange}
                      style={{ width: '100%' }}
                      size="small"
                    >
                      <Option value="LOW">
                        <Tag color="default">Low</Tag>
                      </Option>
                      <Option value="MEDIUM">
                        <Tag color="blue">Medium</Tag>
                      </Option>
                      <Option value="HIGH">
                        <Tag color="orange">High</Tag>
                      </Option>
                      <Option value="CRITICAL">
                        <Tag color="red">Critical</Tag>
                      </Option>
                    </Select>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small">
                    <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>CATEGORY</div>
                    <Select
                      value={selectedTicket.category}
                      onChange={handleCategoryChange}
                      style={{ width: '100%' }}
                      size="small"
                      allowClear
                      placeholder="Select category"
                    >
                      {ticketCategories.map(cat => (
                        <Option key={cat.id} value={cat.id}>
                          {cat.name}
                        </Option>
                      ))}
                    </Select>
                  </Card>
                </Col>
              </Row>

              {/* Account and Assigned To - Inline Editable */}
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Card size="small">
                    <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>ACCOUNT</div>
                    {editingAccount ? (
                      <Select
                        value={selectedTicket.account}
                        onChange={(value) => handleAccountChange(value)}
                        onBlur={() => setEditingAccount(false)}
                        style={{ width: '100%' }}
                        size="small"
                        allowClear
                        placeholder="Select account"
                        showSearch
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        open={editingAccount}
                      >
                        {accounts.map(acc => (
                          <Option key={acc.id} value={acc.id}>
                            {acc.name}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <div
                        onClick={() => setEditingAccount(true)}
                        style={{
                          padding: '4px 8px',
                          cursor: 'pointer',
                          borderRadius: 4,
                          minHeight: 24
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        {selectedTicket.account_name || selectedTicket.account_details?.name || (
                          <span style={{ color: '#999' }}>Click to set account</span>
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small">
                    <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>ASSIGNED TO</div>
                    {editingAssignedTo ? (
                      <Select
                        value={selectedTicket.assigned_to}
                        onChange={(value) => handleAssignedToChange(value)}
                        onBlur={() => setEditingAssignedTo(false)}
                        style={{ width: '100%' }}
                        size="small"
                        allowClear
                        placeholder="Select employee"
                        showSearch
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        open={editingAssignedTo}
                      >
                        {employees.map(emp => (
                          <Option key={emp.id} value={emp.id}>
                            {emp.user?.first_name} {emp.user?.last_name} ({emp.employee_id})
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <div
                        onClick={() => setEditingAssignedTo(true)}
                        style={{
                          padding: '4px 8px',
                          cursor: 'pointer',
                          borderRadius: 4,
                          minHeight: 24
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        {selectedTicket.assigned_to_name || selectedTicket.assigned_to_details?.name || (
                          <span style={{ color: '#999' }}>Click to assign</span>
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Other Information */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(selectedTicket.status_name)}>
                      {selectedTicket.status_name}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Source">
                    {selectedTicket.source_display || selectedTicket.source}
                  </Descriptions.Item>
                  <Descriptions.Item label="Requester" span={2}>
                    {selectedTicket.requester_name} ({selectedTicket.requester_email})
                  </Descriptions.Item>
                  <Descriptions.Item label="SLA Status" span={2}>
                    {selectedTicket.sla_status_display ? (
                      <Tag color={getSLAStatusColor(selectedTicket.sla_status)}>
                        {selectedTicket.sla_status_display}
                      </Tag>
                    ) : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At" span={2}>
                    {selectedTicket.created_at && format(new Date(selectedTicket.created_at), 'MMM dd, yyyy hh:mm a')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Comments */}
              {selectedTicket.comments && selectedTicket.comments.length > 0 && (
                <Card size="small" title="Comments & Activity" style={{ marginBottom: 16 }}>
                  <Timeline>
                    {selectedTicket.comments.map((comment) => (
                      <Timeline.Item
                        key={comment.id}
                        color={
                          comment.comment_type === 'SYSTEM_MESSAGE' ? 'gray' :
                          comment.comment_type === 'EMPLOYEE_REPLY' ? 'green' :
                          comment.comment_type === 'INTERNAL_NOTE' ? 'orange' :
                          'blue'
                        }
                        dot={
                          comment.is_internal ? <ExclamationCircleOutlined /> :
                          comment.comment_type === 'EMPLOYEE_REPLY' ? <CheckCircleOutlined /> :
                          <ClockCircleOutlined />
                        }
                      >
                        <div style={{ marginBottom: 8 }}>
                          <strong>{comment.created_by_name || 'System'}</strong>
                          {comment.is_internal && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>Internal Note</Tag>
                          )}
                          <Tag
                            color={
                              comment.comment_type === 'EMPLOYEE_REPLY' ? 'green' :
                              comment.comment_type === 'CUSTOMER_REPLY' ? 'blue' :
                              'default'
                            }
                            style={{ marginLeft: 8 }}
                          >
                            {comment.comment_type_display || comment.comment_type}
                          </Tag>
                          <span style={{ color: '#999', marginLeft: 8 }}>
                            {comment.created_at && format(new Date(comment.created_at), 'MMM dd, yyyy hh:mm a')}
                          </span>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                          {comment.message}
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>

                  <Divider />

                  <Button
                    icon={<MessageOutlined />}
                    type="primary"
                    onClick={() => setCommentModalVisible(true)}
                    block
                  >
                    Add Comment
                  </Button>
                </Card>
              )}
            </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading ticket details...</p>
          </div>
        )}
      </Drawer>

      {/* Assign Modal */}
      <Modal
        title="Assign Ticket"
        open={assignModalVisible}
        onOk={handleAssign}
        onCancel={() => setAssignModalVisible(false)}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="employee_id"
            label="Assign to Employee"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select employee"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {employees.map(emp => (
                <Option key={emp.id} value={emp.id}>
                  {emp.user?.first_name} {emp.user?.last_name} ({emp.employee_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="assigned_team" label="Team (Optional)">
            <Input placeholder="e.g., Technical Support, Billing" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Status Change Modal */}
      <Modal
        title="Change Ticket Status"
        open={statusModalVisible}
        onOk={handleStatusChange}
        onCancel={() => setStatusModalVisible(false)}
      >
        <Form form={statusForm} layout="vertical">
          <Form.Item
            name="status_id"
            label="New Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status">
              {ticketStatuses.map(status => (
                <Option key={status.id} value={status.id}>
                  {status.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Comment Modal */}
      <Modal
        title="Add Comment"
        open={commentModalVisible}
        onOk={handleAddComment}
        onCancel={() => setCommentModalVisible(false)}
        width={600}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Form form={commentForm} layout="vertical">
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter a message' }]}
          >
            <TextArea rows={6} placeholder="Type your message..." />
          </Form.Item>

          <Form.Item name="is_internal" valuePropName="checked" initialValue={false}>
            <Checkbox>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 4 }} />
              Internal Note (not visible to customer)
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Convert to Task Modal */}
      <Modal
        title="Convert to Task"
        open={taskModalVisible}
        onOk={handleConvertToTasks}
        onCancel={() => setTaskModalVisible(false)}
        width={600}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Form form={taskForm} layout="vertical">
          <Form.Item
            name="project_id"
            label="Project"
            rules={[{ required: true, message: 'Please select a project' }]}
          >
            <Select placeholder="Select project">
              {projects.map(proj => (
                <Option key={proj.id} value={proj.id}>
                  {proj.project_code} - {proj.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="title" label="Task Title">
            <Input placeholder={selectedTicket?.title} />
          </Form.Item>

          <Form.Item name="description" label="Task Description">
            <TextArea rows={4} placeholder={selectedTicket?.description} />
          </Form.Item>

          <Form.Item name="notes" label="Notes (Optional)">
            <TextArea rows={2} placeholder="Why this task was created from the ticket..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportTickets;
